import { z, type ZodType } from "zod";

/**
 * Generic schema factory for `InfiniteData<T>` blobs as react-query stores
 * them internally. Used by the global infinite-query persister to validate
 * cached rows on read and to validate blobs before write.
 *
 * Returns the raw `z.object({...})` (not cast to `ZodType<InfiniteData<T>>`)
 * because `readInfiniteQueryCache` accepts a `ZodType<InfiniteData<unknown>>`
 * — deliberately variance-friendly so wider passthrough page schemas (where
 * `z.infer<schema>` ends up wider than the strict `RouterOutput` page type)
 * pass without an `as any` cast at the call site.
 *
 * Example:
 *   const schema = infiniteDataSchema(forYouPageSchema, z.number().optional());
 */
export function infiniteDataSchema<T>(
  pageSchema: ZodType<T>,
  cursorSchema: ZodType<unknown>,
) {
  return z.object({
    pages: z.array(pageSchema),
    // react-query stores `pageParams[0]` as the value passed to
    // `initialPageParam` — typically `undefined`. After JSON.stringify the
    // `undefined` becomes `null` (arrays preserve length). So even when the
    // cursor shape is e.g. `z.number()`, the first slot is null. Accept
    // `cursorSchema | null` to validate post-roundtrip blobs correctly;
    // without this, every infinite-query write fails schema parse and the
    // persister silently never writes anything to SQLite.
    pageParams: z.array(z.union([cursorSchema, z.null()])),
  });
}

/**
 * Generic page-shape factory for the very common
 * `{ data: T[], endCursor?: C, startCursor?: C }` server response shape that
 * lissen's paginated catalog routes return.
 */
export function endCursorPageSchema<T>(
  itemSchema: ZodType<T>,
  cursorType: ZodType<unknown>,
) {
  return z.object({
    data: z.array(itemSchema),
    endCursor: cursorType.optional(),
    startCursor: cursorType.optional(),
  });
}
